import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

// "EWEMEL: 2, COLHQ: 1" for the Committed cell's tooltip.
const formatCommittedByLocation = (committedByLocation = {}) => Object.entries(committedByLocation)
  .map(([location, units]) => `${location}: ${units}`)
  .join(', ') || undefined;

const SOHTable = ({
  groupedData,
  loadingState,
  allMonthStrings,
  weekKeys,
  forecastRegions,
  forecastMonths,
  findSKUStatus,
  region,
  visibleColumns
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubCategories, setExpandedSubCategories] = useState({});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleSubCategory = (category, subCategory) => {
    const key = `${category}-${subCategory}`;
    setExpandedSubCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Available': return 'text-green-600 bg-green-100 px-2 py-1 rounded-md font-medium';
      case 'Off Site': return 'text-gray-600 bg-gray-100 px-2 py-1 rounded-md font-medium';
      case 'Low Stock': return 'text-orange-600 bg-orange-100 px-2 py-1 rounded-md font-medium';
      case 'Out of Stock': return 'text-red-600 bg-red-100 px-2 py-1 rounded-md font-medium';
      default: return 'text-gray-600 bg-gray-100 px-2 py-1 rounded-md font-medium';
    }
  };

  const renderTableHeaders = () => (
    <thead className="thead-light sticky top-0 z-30">
      <tr>
        <th className="sticky left-0 z-20 bg-gray-100 shadow-md min-w-[150px] text-left px-3 py-2">SKU</th>
        <th className="sticky left-[150px] z-20 bg-gray-100 shadow-md min-w-[150px] text-left px-3 py-2">Product</th>
        {visibleColumns.status && <th className="bg-gray-100 text-left px-3 py-2 sticky top-0">Status</th>}
        {visibleColumns.category && <th className="bg-gray-100 text-left px-3 py-2 sticky top-0">Category</th>}
        {visibleColumns.subCategory && <th className="bg-gray-100 text-left px-3 py-2 sticky top-0">Sub Category</th>}
        {visibleColumns.color && <th className="bg-gray-100 text-left px-3 py-2 sticky top-0">Color</th>}
        {visibleColumns.rrp && <th className="bg-gray-100 text-right px-3 py-2 sticky top-0">RRP</th>}
        {visibleColumns.auCog && <th className="bg-gray-100 text-right px-3 py-2 sticky top-0">AU COG</th>}
        {visibleColumns.usCog && <th className="bg-gray-100 text-right px-3 py-2 sticky top-0">US COG</th>}

        {visibleColumns.monthlyData && allMonthStrings.map((month, i) => (
          <th className="bg-yellow-100 text-center px-3 py-2 sticky top-0" key={"_month_" + i}>{month}</th>
        ))}

        {visibleColumns.weeklyData && weekKeys.map((weekKey) => (
          <th className="bg-blue-100 text-center px-3 py-2 sticky top-0" key={`week_${weekKey}`}>{weekKey.toUpperCase()}</th>
        ))}
        {visibleColumns.weeklyData && <th className="bg-blue-100 text-center px-3 py-2 sticky top-0">MTD</th>}

        {visibleColumns.forecastData && forecastRegions.map((region) => 
          forecastMonths.map((month) => (
            <th className="bg-red-100 text-center px-3 py-2 sticky top-0" key={`forecast_${region}_${month}`}>
              {month} ({region})
            </th>
          ))
        )}
        
        {visibleColumns.stockData && (
          <>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">Total Stock</th>
            <th
              className="bg-green-100 text-center px-3 py-2 sticky top-0"
              title="Picked or sent to a 3PL, not yet shipped. Still held, but not in Total Stock."
            >
              Committed
            </th>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">Unallocated</th>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">Spot Cover</th>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">Fwd Cover</th>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">Total Upcoming</th>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">Total Stock (After Incoming)</th>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">Month Cover</th>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">LY Sales</th>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">Var LY Sales</th>
            <th className="bg-green-100 text-center px-3 py-2 sticky top-0">Var LY Sales %</th>
          </>
        )}
      </tr>
    </thead>
  );

  const renderDataRow = (status, i) => (
    <tr className="py-8 hover:bg-gray-50" style={{ padding: '16px 0' }} key={"_line_" + `${i}_` + `${status.sku}`}>
      <td className="sticky left-0 z-20 bg-white shadow-md min-w-[150px] text-left px-3 py-2"> {status.sku} </td>
      <td className="sticky left-[150px] z-20 bg-white shadow-md min-w-[150px] text-left px-3 py-2 truncate"> {status.productName} </td>
      {visibleColumns.status && (
        <td className="text-left px-3 py-2">
          <span className={getStatusClass(findSKUStatus(status.sku, region))}>
            {findSKUStatus(status.sku, region)}
          </span>
        </td>
      )}
      {visibleColumns.category && <td className="text-left px-3 py-2"> {status.productCategory} </td>}
      {visibleColumns.subCategory && <td className="text-left px-3 py-2"> {status.productSubCategory} </td>}
      {visibleColumns.color && <td className="text-left px-3 py-2"> {status.color}</td>}
      {visibleColumns.rrp && <td className="text-right px-3 py-2"> {status.retailPrice}</td>}
      {visibleColumns.auCog && <td className="text-right px-3 py-2"> {status.auCog}</td>}
      {visibleColumns.usCog && <td className="text-right px-3 py-2"> {status.usCog}</td>}
      
      {visibleColumns.monthlyData && status.monthCounts.map((month, i) => (
        <td className="bg-yellow-50 text-center px-3 py-2" key={"_month_" + `${i}_` + `${status.sku}`}>
          {month.count === 0 ? '' : month.count}
        </td>
      ))}
      
      {visibleColumns.weeklyData && weekKeys.map((weekKey) => (
        <td className="bg-blue-50 text-center px-3 py-2" key={`week_${weekKey}_${status.sku}`}>
          {status.weeklyData && status.weeklyData[weekKey] !== 0 ? status.weeklyData[weekKey] : ''}
        </td>
      ))}
      {visibleColumns.weeklyData && <td className="bg-blue-50 text-center px-3 py-2">{status.weeklyData && status.weeklyData.mtd !== 0 ? status.weeklyData.mtd : ''}</td>}
      
      {visibleColumns.forecastData && forecastRegions.map((region) => 
        forecastMonths.map((month) => (
          <td className="bg-red-50 text-center px-3 py-2" key={`forecast_${region}_${month}_${status.sku}`}>
            {status.forecasts && status.forecasts[region] && status.forecasts[region][month] !== 0 
              ? status.forecasts[region][month] 
              : ''}
          </td>
        ))
      )}
      
      {visibleColumns.stockData && (
        <>
          <td className="bg-green-50 text-right px-3 py-2"> {status.totalStock}</td>
          <td className="bg-green-50 text-right px-3 py-2" title={formatCommittedByLocation(status.committedByLocation)}>
            {status.committed}
          </td>
          <td className="bg-green-50 text-right px-3 py-2"> {status.unallocated}</td>
          <td className="bg-green-50 text-right px-3 py-2"> {status.spotCover}</td>
          <td className="bg-green-50 text-right px-3 py-2"> {status.fwdCover}</td>
          <td className="bg-green-50 text-right px-3 py-2"> {status.totalUpcoming}</td>
          <td className="bg-green-50 text-right px-3 py-2"> {status.totalStockAfterIncoming}</td>
          <td className="bg-green-50 text-right px-3 py-2"> {status.monthCover}</td>
          <td className="bg-green-50 text-right px-3 py-2"> {status.lyMtd}</td>
          <td className="bg-green-50 text-right px-3 py-2"> {status.varLy}</td>
          <td className="bg-green-50 text-right px-3 py-2"> {status.varLyPercentage}</td>
        </>
      )}
    </tr>
  );

  return (
    <div className="table-responsive" style={{ 
      overflow: 'auto', 
      maxWidth: '100%', 
      maxHeight: 'calc(70vh)',
      position: 'relative'
    }}>
      <table className="table table-centered table-nowrap" style={{ position: 'relative' }}>
        {renderTableHeaders()}
        <tbody>
          {loadingState ? (
            Object.keys(groupedData).map(category => {
              const isExpanded = expandedCategories[category];
              return (
                <React.Fragment key={category}>
                  {/* Category Header */}
                  <tr
                    className="bg-gray-800 text-white font-bold cursor-pointer hover:bg-gray-700"
                    onClick={() => toggleCategory(category)}
                  >
                    <td className="sticky left-0 z-20 bg-gray-800 shadow-md min-w-[150px] text-left px-3 py-3">
                      <div className="flex items-center">
                        {isExpanded ? <ChevronDown className="h-4 w-4 mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />}
                        {category || 'Uncategorized'}
                      </div>
                    </td>
                    <td className="sticky left-[150px] z-20 bg-gray-800 shadow-md min-w-[150px] text-left px-3 py-3"></td>
                    {visibleColumns.status && <td className="bg-gray-800 px-3 py-3"></td>}
                    {visibleColumns.category && <td className="bg-gray-800 px-3 py-3"></td>}
                    {visibleColumns.subCategory && <td className="bg-gray-800 px-3 py-3"></td>}
                    {visibleColumns.color && <td className="bg-gray-800 px-3 py-3"></td>}
                    {visibleColumns.rrp && <td className="bg-gray-800 px-3 py-3"></td>}
                    {visibleColumns.auCog && <td className="bg-gray-800 px-3 py-3"></td>}
                    {visibleColumns.usCog && <td className="bg-gray-800 px-3 py-3"></td>}
                    {visibleColumns.monthlyData && allMonthStrings.map((month, i) => (
                      <td className="bg-gray-800 px-3 py-3" key={`cat_month_${i}`}></td>
                    ))}
                    {visibleColumns.weeklyData && weekKeys.map((weekKey) => (
                      <td className="bg-gray-800 px-3 py-3" key={`cat_week_${weekKey}`}></td>
                    ))}
                    {visibleColumns.weeklyData && <td className="bg-gray-800 px-3 py-3"></td>}
                    {visibleColumns.forecastData && forecastRegions.flatMap(region =>
                      forecastMonths.map(month => (
                        <td className="bg-gray-800 px-3 py-3" key={`cat_forecast_${region}_${month}`}></td>
                      ))
                    )}
                    {visibleColumns.stockData && (
                      <>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                        <td className="bg-gray-800 px-3 py-3"></td>
                      </>
                    )}
                  </tr>
                  
                  {isExpanded && Object.keys(groupedData[category]).map(subCategory => {
                    const subKey = `${category}-${subCategory}`;
                    const isSubExpanded = expandedSubCategories[subKey];
                    return (
                      <React.Fragment key={subKey}>
                        {/* Sub Category Header */}
                        <tr
                          className="bg-gray-200 font-semibold cursor-pointer hover:bg-gray-300"
                          onClick={() => toggleSubCategory(category, subCategory)}
                        >
                          <td className="sticky left-0 z-20 bg-gray-200 shadow-md min-w-[150px] text-left px-6 py-2">
                            <div className="flex items-center">
                              {isSubExpanded ? <ChevronDown className="h-4 w-4 mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />}
                              {subCategory || 'No Sub-category'}
                            </div>
                          </td>
                          <td className="sticky left-[150px] z-20 bg-gray-200 shadow-md min-w-[150px] text-left px-6 py-2"></td>
                          {visibleColumns.status && <td className="bg-gray-200 px-6 py-2"></td>}
                          {visibleColumns.category && <td className="bg-gray-200 px-6 py-2"></td>}
                          {visibleColumns.subCategory && <td className="bg-gray-200 px-6 py-2"></td>}
                          {visibleColumns.color && <td className="bg-gray-200 px-6 py-2"></td>}
                          {visibleColumns.rrp && <td className="bg-gray-200 px-6 py-2"></td>}
                          {visibleColumns.auCog && <td className="bg-gray-200 px-6 py-2"></td>}
                          {visibleColumns.usCog && <td className="bg-gray-200 px-6 py-2"></td>}
                          {visibleColumns.monthlyData && allMonthStrings.map((month, i) => (
                            <td className="bg-gray-200 px-6 py-2" key={`subcat_month_${i}`}></td>
                          ))}
                          {visibleColumns.weeklyData && weekKeys.map((weekKey) => (
                            <td className="bg-gray-200 px-6 py-2" key={`subcat_week_${weekKey}`}></td>
                          ))}
                          {visibleColumns.weeklyData && <td className="bg-gray-200 px-6 py-2"></td>}
                          {visibleColumns.forecastData && forecastRegions.flatMap(region =>
                            forecastMonths.map(month => (
                              <td className="bg-gray-200 px-6 py-2" key={`subcat_forecast_${region}_${month}`}></td>
                            ))
                          )}
                          {visibleColumns.stockData && (
                            <>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                              <td className="bg-gray-200 px-6 py-2"></td>
                            </>
                          )}
                        </tr>
                        
                        {/* Data Rows */}
                        {isSubExpanded && groupedData[category][subCategory].map((status, i) => 
                          renderDataRow(status, i)
                        )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })
          ) : (
            // Loading placeholders
            [...Array(10)].map((_, i) => (
              <tr className="py-8" style={{ padding: '16px 0' }} key={"_line_loading_" + i}>
                <td className="sticky left-0 z-20 bg-gray-100 shadow-md min-w-[150px] text-left px-3 py-2"> <div className="placeholder" /> </td>
                <td className="sticky left-[150px] z-20 bg-gray-100 shadow-md min-w-[150px] text-left px-3 py-2"> <div className="placeholder" /> </td>
                {/* Add more placeholder cells as needed */}
                {Array.from({ length: 16 }, (_, index) => (
                  <td className="bg-gray-100 text-center px-3 py-2" key={`placeholder_${index}`}><div className="placeholder" /></td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SOHTable;
